import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Cookies from 'universal-cookie';
import { AppCookies } from '@enums/cookies';
import logger from '@helpers/utils/logger/client';
import Environment from '@config/environment';

export interface OrderDataV2 {
  orderId?: string;
  orderAmountFormatted?: string;
  datalayer?: {
    order_amount_ati_with_sf?: string;
    order_amount_ati_without_sf?: string;
    basket_total_value?: string;
    pageUrl?: string;
  };
}

/**
 * Validates payment data structure and orderId match
 * @param data - Unknown data to validate
 * @param expectedOrderId - Expected orderId to match against
 * @returns true if data is valid OrderDataV2 with matching orderId
 */
export const isValidPaymentData = (data: unknown, expectedOrderId: string): boolean => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const { orderId, orderAmountFormatted } = data as { orderId?: unknown; orderAmountFormatted?: unknown };

  return (
    typeof orderId === 'string' &&
    typeof orderAmountFormatted === 'string' &&
    orderId.trim() !== '' &&
    orderAmountFormatted.trim() !== '' &&
    orderId === expectedOrderId // Must match query param orderId
  );
};

/**
 * Validates that all required datalayer fields are present
 * Logs to Datadog if any tracking fields are missing
 * @param data - OrderDataV2 to validate
 * @param userId - User ID for logging context
 */
export const validateDatalayerFields = (data: OrderDataV2, userId: string): void => {
  const missingFields: string[] = [];

  if (!data.orderId) {
    missingFields.push('orderId');
  }
  if (!data.datalayer?.basket_total_value) {
    missingFields.push('basket_total_value');
  }
  if (!data.datalayer?.order_amount_ati_with_sf) {
    missingFields.push('order_amount_ati_with_sf');
  }
  if (!data.datalayer?.order_amount_ati_without_sf) {
    missingFields.push('order_amount_ati_without_sf');
  }
  if (!data.datalayer?.pageUrl) {
    missingFields.push('pageUrl');
  }

  if (missingFields.length > 0) {
    const error = new Error('[Payment V2] Missing datalayer tracking fields');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- logger is untyped CommonJS module
    logger.error(error, {
      context: 'order-confirmation-datalayer',
      missingFields,
      userId,
      orderId: data.orderId || 'undefined',
      v2OrderDataKeys: Object.keys(data),
    });
  }
};

/**
 * Loads and validates payment order data from cookies or sessionStorage
 * @param params - Parameters for loading order data
 * @returns OrderDataV2 if found and valid, null otherwise
 */
export const loadPaymentOrderData = (params: {
  queryOrderId: string;
  userId: string;
  cookies: Cookies;
}): OrderDataV2 | null => {
  const { queryOrderId, userId, cookies } = params;
  const cookieKey = `${AppCookies.PAYMENT_ORDER_DATA}_${userId}`;
  const sessionStorageKey = cookieKey;

  // Step 1: Try cookie first (source of truth from checkout)
  const paymentDataRaw = cookies.get(cookieKey) as string | undefined;

  if (paymentDataRaw) {
    // Parse cookie data
    let paymentData: unknown;
    try {
      paymentData = typeof paymentDataRaw === 'string' ? (JSON.parse(paymentDataRaw) as unknown) : paymentDataRaw;
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- logger is untyped CommonJS module
      logger.error(e, '[Payment V2] Failed to parse cookie data', {
        context: 'order-confirmation-cookie-parse',
        userId,
      });
      return null;
    }

    // Validate data structure and orderId match
    if (!isValidPaymentData(paymentData, queryOrderId)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- logger is untyped CommonJS module
      logger.error(new Error('[Payment V2] Invalid payment data structure or orderId mismatch in cookie'), {
        context: 'order-confirmation-cookie-validation',
        userId,
        queryOrderId,
      });
      return null;
    }

    // Valid cookie data - save to sessionStorage for page reloads
    try {
      sessionStorage.setItem(sessionStorageKey, JSON.stringify(paymentData));
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- logger is untyped CommonJS module
      logger.error(e, '[Payment V2] Failed to save to sessionStorage', {
        context: 'order-confirmation-sessionStorage-write',
        userId,
      });
    }

    // Delete cookie immediately (good practice: reduces bandwidth, privacy compliance, one-time use data)
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const domain = isLocalhost ? hostname : Environment.cookieOptions.domain;
    const secure = window.location.protocol === 'https:';
    cookies.remove(cookieKey, { domain, path: '/', secure });

    return paymentData as OrderDataV2;
  }

  // Step 2: No cookie - try sessionStorage (for page reloads after cookie was deleted)
  const sessionStorageData = sessionStorage.getItem(sessionStorageKey);
  if (!sessionStorageData) {
    return null;
  }

  // Parse sessionStorage data
  let paymentData: unknown;
  try {
    paymentData = JSON.parse(sessionStorageData) as unknown;
  } catch (e) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- logger is untyped CommonJS module
    logger.error(e, '[Payment V2] Failed to parse sessionStorage data', {
      context: 'order-confirmation-sessionStorage-parse',
      userId,
    });
    // Clean up corrupted data
    try {
      sessionStorage.removeItem(sessionStorageKey);
    } catch {
      // Silent fail
    }
    return null;
  }

  // Validate data structure and orderId match
  if (!isValidPaymentData(paymentData, queryOrderId)) {
    // Invalid data or wrong order - remove it
    sessionStorage.removeItem(sessionStorageKey);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- logger is untyped CommonJS module
    logger.error(new Error('[Payment V2] Invalid payment data or orderId mismatch in sessionStorage'), {
      context: 'order-confirmation-sessionStorage-validation',
      userId,
      queryOrderId,
    });
    return null;
  }

  // Valid sessionStorage data - use it
  return paymentData as OrderDataV2;
};

/**
 * Custom hook to load and manage Payment V2 order data
 * Handles cookie → sessionStorage pattern for order confirmation page
 * See README.md for detailed explanation
 *
 * @param userId - Authenticated user ID
 * @returns OrderDataV2 object or null if not available/valid
 */
const usePaymentOrderData = (userId: string | null): OrderDataV2 | null => {
  const router = useRouter();
  const [orderData, setOrderData] = useState<OrderDataV2 | null>(null);
  const isPaymentV2 = router.query?.v === '2';

  useEffect(() => {
    if (!isPaymentV2 || !userId) {
      return;
    }

    // Step 1: Get and validate orderId from query params
    const queryOrderId = router.query.orderId as string | undefined;
    if (!queryOrderId) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- logger is untyped CommonJS module
      logger.error(
        new Error('[Payment V2] Missing orderId in query params - displaying confirmation page with empty data'),
        {
          context: 'order-confirmation-missing-orderId',
          userId,
        },
      );
      // Continue without data - better UX to show confirmation page than error page
      return;
    }

    const cookies = new Cookies();
    const data = loadPaymentOrderData({ queryOrderId, userId, cookies });

    if (data) {
      // Validate datalayer fields are complete for tracking
      validateDatalayerFields(data, userId);
      setOrderData(data);
    }
  }, [isPaymentV2, userId, router.query.orderId]);

  return orderData;
};

export default usePaymentOrderData;
